<script setup lang="ts">
import { inject, reactive, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, maxLength } from '@vuelidate/validators';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { resetTimer } from '@/utils/dateUtils';
import { PACKAGE_LAND_ONLY, STATUS_CLOSED } from '@/constants';
import { useTourManagementStore } from '@/store/TourManagementStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import InputText from 'primevue/inputtext';
import LoadingSpinner from '../LoadingSpinner.vue';
import HotelDateTypes from './hotels/HotelDateTypes.vue';
import TourTarget from './tour/TourTarget.vue';
import TourInfoEn from './tour/TourInfoEn.vue';
import TourInfoFr from './tour/TourInfoFr.vue';
import TourInfoGroup from './tour/TourInfoGroup.vue';

const dialogRef = inject('dialogRef') as Ref;
const onApply = dialogRef?.value?.options?.emits?.onApply;
const isEdit = dialogRef?.value?.options?.data?.isEdit;
const tourData = dialogRef?.value?.options?.data?.data;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const tourManagementStore = useTourManagementStore();
const isLoading = ref(false);
const errorMessage = ref('');
const hasError = ref(false);

const formatTourData = (data: any) => {
  return {
    ...data,
    quoteDate: formatListDate(data.quoteDate),
    travelDate: formatListDate(data.travelDate),
    blackoutDate: formatListDate(data.blackoutDate)
  };
};

const formatListDate = (listDate: any) => {
  if (listDate?.length) {
    return listDate?.map((date: any) => {
      return {
        start: new Date(DateTime.fromISO(date.start)),
        end: new Date(DateTime.fromISO(date.end))
      };
    });
  } else {
    return [];
  }
};

const emptyTourFactory = () => {
  return {
    svId: '',
    ttsId: '',
    tourNameEn: '',
    tourNameFr: '',
    tourInclusionEn: '',
    tourInclusionFr: '',
    tourDurationEn: '',
    tourDurationFr: '',
    tourType: '',
    guided: '',
    visitedCitiesEn: '',
    visitedCitiesFr: '',
    tourLinkEn: '',
    tourLinkFr: '',
    tourDescriptionEn: '',
    tourDescriptionFr: '',
    destinationCode: '',
    cacheType: 'MANUAL',
    maxRooms: 0,
    quoteDate: [],
    travelDate: [],
    blackoutDate: [],
    groupTypes: [],
    tourApiCallOrder: PACKAGE_LAND_ONLY,
    imageLink: '',
    instaQuote: STATUS_CLOSED,
    status: STATUS_CLOSED,
    tourOnly: true
  };
};

const tour: any = reactive(tourData ? cloneDeep(formatTourData(tourData)) : emptyTourFactory());

const isAlphaNumeric = helpers.regex(/^[a-zA-Z0-9]+$/);

const rules = ref({
  ttsId: { required, maxLength: maxLength(12), validationSymbolSpecify: helpers.withMessage(t('validate.wong_format'), isAlphaNumeric) },
  tourNameEn: { required },
  svId: { required },
  tourNameFr: { required },
  tourType: { required },
  tourInclusionEn: { required },
  tourInclusionFr: { required },
  guided: { required },
  tourLinkEn: { required },
  tourLinkFr: { required },
  tourDurationEn: { required },
  tourDurationFr: { required },
  visitedCitiesEn: { required },
  visitedCitiesFr: { required },
  maxRooms: { required },
  tourDescriptionEn: { required },
  tourDescriptionFr: { required },
  groupTypes: { required },
  blackoutDate: {
    $each: helpers.forEach({
      end: { required: helpers.withMessage(t('validate.required'), required) },
      start: { required: helpers.withMessage(t('validate.required'), required) }
    })
  },
  travelDate: {
    $each: helpers.forEach({
      end: { required: helpers.withMessage(t('validate.required'), required) },
      start: { required: helpers.withMessage(t('validate.required'), required) }
    })
  },
  quoteDate: {
    $each: helpers.forEach({
      end: { required: helpers.withMessage(t('validate.required'), required) },
      start: { required: helpers.withMessage(t('validate.required'), required) }
    })
  }
});

const v$: any = useVuelidate(rules.value, tour);

const addNewDate = (type: string) => {
  tour[type].push({
    start: null,
    end: null
  });
};

const removeDate = (type: string, index: number) => {
  tour[type] = tour[type].filter((_item: any, indexDate: number) => indexDate !== index);
};

const validateCheckbox = (isTourOnly: any) => {
  tour.tourOnly = isTourOnly;
};

const validateSwitch = (event: any) => {
  if (event.switchType === 'instaquote') {
    tour.instaQuote = event.value.instaQuote;
  } else {
    tour.status = event.value.status;
  }
};

const validateInput = () => {
  hasError.value = false;
  errorMessage.value = '';

  if (!tour.ttsId) {
    hasError.value = true;
    errorMessage.value = t('validate.required');
  } else if (tour.ttsId.length > 12) {
    hasError.value = true;
    errorMessage.value = v$.value.ttsId.maxLength.$message;
  } else if (!/^[a-zA-Z0-9]+$/.test(tour.ttsId)) {
    hasError.value = true;
    errorMessage.value = t('validate.wong_format');
  }
};

const formatDateZeroHour = (listDate: any) => {
  if (listDate?.length) {
    return listDate?.map((date: any) => {
      return {
        start: resetTimer(date.start),
        end: resetTimer(date.end)
      };
    });
  } else {
    return [];
  }
};

const handleCreateTourDetails = async (data: any) => {
  const payload = {
    ...data,
    ttsId: data.ttsId.toUpperCase(),
    quoteDate: formatDateZeroHour(data.quoteDate),
    travelDate: formatDateZeroHour(data.travelDate),
    blackoutDate: formatDateZeroHour(data.blackoutDate)
  };
  isLoading.value = true;
  tourManagementStore
    .addTourDetail(payload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      onApply();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    })
    .finally(() => (isLoading.value = false));
};

const handleUpdateTourDetails = (data: any) => {
  isLoading.value = true;
  const payload = {
    oldCompositeKey: {
      pk: data.pk,
      sk: data.sk
    },
    data: {
      ...data,
      quoteDate: formatDateZeroHour(data.quoteDate),
      travelDate: formatDateZeroHour(data.travelDate),
      blackoutDate: formatDateZeroHour(data.blackoutDate)
    }
  };
  tourManagementStore
    .editTourDetail(payload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      onApply();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    })
    .finally(() => (isLoading.value = false));
};

const handleCallSubmit = async (data: any, isEditModal: boolean) => {
  v$.value.$validate();
  if (!tour.ttsId) {
    hasError.value = true;
    errorMessage.value = t('validate.required');
  }
  if (!v$.value.$error) {
    if (isEditModal) {
      handleUpdateTourDetails(data);
    } else {
      handleCreateTourDetails(data);
    }
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.fill_required') });
  }
};
</script>
<template>
  <div class="tour-management">
    <div class="add-tour-modal">
      <LoadingSpinner v-if="isLoading" />
      <div>
        <Fluid>
          <div class="field grid">
            <div class="col-4">
              <label for="tts-code" class="font-title">{{ $t('management.tour.tour_code') }} <span class="text-primary">*</span> </label>
              <InputText id="tts-code" :disabled="isEdit" class="uppercase" :class="v$.ttsId?.$error ? 'p-invalid w-full' : 'w-full'" type="text" v-model="tour.ttsId" @blur="validateInput()" />
              <p class="text-red-500 pt-1" v-if="hasError">{{ errorMessage }}</p>
            </div>
            <div class="col-4"></div>
            <div class="col-4">
              <label for="sv-code" class="font-title">{{ $t('management.hotel.softVoyage_code') }} <span class="text-primary">*</span></label>
              <InputText id="sv-code" :disabled="isEdit" :class="v$.svId?.$error ? 'p-invalid w-full' : 'w-full'" type="text" v-model="tour.svId" />
              <p class="text-red-500 pt-1" v-if="v$.svId?.$error">{{ $t('validate.required') }}</p>
            </div>
          </div>
        </Fluid>
        <Fluid>
          <div class="flex grid">
            <div class="col-4">
              <TourInfoEn :inputValue="tour" :validate="v$" />
            </div>
            <div class="col-4">
              <TourInfoFr :inputValue="tour" :validate="v$" />
            </div>
            <div class="col-4">
              <TourInfoGroup :inputValue="tour" :validate="v$" />
            </div>
          </div>
        </Fluid>
      </div>
      <Fluid>
        <div class="field grid border-dot-line">
          <div class="col-4">
            <HotelDateTypes :title="$t('management.tour.quote_dates')" :dateRanges="tour.quoteDate" @addNewDate="addNewDate('quoteDate')" @removeDate="removeDate('quoteDate', $event)" />
          </div>
          <div class="col-4">
            <HotelDateTypes :title="$t('hotel_contract.travel_date_range')" :dateRanges="tour.travelDate" @addNewDate="addNewDate('travelDate')" @removeDate="removeDate('travelDate', $event)" />
          </div>
          <div class="col-4">
            <HotelDateTypes :title="$t('hotel_contract.black_out_date')" :dateRanges="tour.blackoutDate" @addNewDate="addNewDate('blackoutDate')" @removeDate="removeDate('blackoutDate', $event)" />
          </div>
        </div>
      </Fluid>
      <Fluid>
        <TourTarget
          :tourOnly="tour.tourOnly"
          :tourStatus="{ status: tour.status, instaQuote: tour.instaQuote }"
          :isHideInstaQuote="false"
          :instaPermissions="authorizations.tour_detail_configuration.insta_quote_notification"
          :statusPermissions="authorizations.tour_detail_configuration.status_notification"
          :isEdit="false"
          :targetLabel="$t('management.tour.create_tour_in')"
          :checkboxLabel="$t('management.tour.tour_only')"
          @validateCheckbox="validateCheckbox($event)"
          @validateSwitch="validateSwitch($event)"
        />
      </Fluid>
      <div class="text-center mt-5">
        <Button :loading="isLoading" :label="$t('common.buttons.submit')" @click="handleCallSubmit(tour, isEdit)" data-testid="submit-btn"></Button>
      </div>
    </div>
  </div>
</template>
